// src/components/jobDetails/NewRunModal.jsx
import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import API from "../../api/api";

export default function NewRunModal({ jobId, wellInfo, runs, onClose, onRunCreated }) {
  const [modalActiveTab, setModalActiveTab] = useState('run');
  const [isRunFormComplete, setIsRunFormComplete] = useState(false);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [northReferences, setNorthReferences] = useState([]);
  const [surveyRunIns, setSurveyRunIns] = useState([]);
  const [holeSections, setHoleSections] = useState([]);
  const [minimumIds, setMinimumIds] = useState([]);
  const [wellTypes, setWellTypes] = useState([]);
  
  const [newRunData, setNewRunData] = useState({
    survey_from: "",
    survey_to: "",
    survey_meterage: "",
    survey_type: "",
    proposed_direction: "",
    elevation_referred_to: "Mean Sea Level(MSL)",
    reference_datum: "RKB/DFE(Drill Floor Elevation)",
    reference_height: "",
    geodetic_system: "Universal Transverse Mercator",
    geodetic_datum: "PSD 93",
    map_zone: "Zone 40N(54E to 60E)",
    max_depth: "",
    max_inclination: "",
    max_temp: "",
    north_reference: "",
    exp_inclination: "",
    hole_section: "",
    survey_run_in: "",
    minimum_id: "",
    tie_on: {
      md: "", tvd: "", inc: "", az: "", ns: "", ew: ""
    }
  });

  useEffect(() => {
    async function loadDropdownData() {
      try {
        const [northRefRes, surveyRunInRes, holeSectionRes, minimumIdRes, surveyTypesRes, wellTypesRes] = await Promise.all([
          API.get('northreference/'),
          API.get('surveyrunin/'),
          API.get('holesection/'),
          API.get('minimumid/'),
          API.get('surveytype/'),
          API.get('welltype/')
        ]);

        setNorthReferences(northRefRes.data);
        setSurveyRunIns(surveyRunInRes.data);
        setHoleSections(holeSectionRes.data);
        setMinimumIds(minimumIdRes.data);
        setSurveyTypes(surveyTypesRes.data);
        setWellTypes(wellTypesRes.data);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    }

    loadDropdownData();
  }, []);

  // Auto-calculate survey meterage
  useEffect(() => {
    const surveyFrom = parseFloat(newRunData.survey_from);
    const surveyTo = parseFloat(newRunData.survey_to);
    
    if (!isNaN(surveyFrom) && !isNaN(surveyTo) && surveyTo > surveyFrom) {
      const meterage = (surveyTo - surveyFrom).toFixed(2);
      setNewRunData(prev => ({ ...prev, survey_meterage: meterage }));
    } else {
      setNewRunData(prev => ({ ...prev, survey_meterage: "" }));
    }
  }, [newRunData.survey_from, newRunData.survey_to]);

  const validateRunForm = () => {
    const requiredFields = [
      'survey_from',
      'survey_to',
      'survey_meterage',
      'survey_type',
      'hole_section',
      'survey_run_in',
      'minimum_id'
    ];
    
    const isValid = requiredFields.every(field => 
      newRunData[field] && newRunData[field].toString().trim() !== ''
    );
    
    if (newRunData.survey_from && newRunData.survey_to) {
      const from = parseFloat(newRunData.survey_from);
      const to = parseFloat(newRunData.survey_to);
      if (from >= to) {
        toast.error("Survey From must be less than Survey To");
        return false;
      }
    }
    
    setIsRunFormComplete(isValid);
    return isValid;
  };

  useEffect(() => {
    validateRunForm();
  }, [newRunData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRunData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTieOnChange = (e) => {
    const { name, value } = e.target;
    setNewRunData((prev) => ({
      ...prev,
      tie_on: {
        ...prev.tie_on,
        [name]: value,
      },
    }));
  };

  const handleTieOnFromSurface = () => {
    setNewRunData((prevData) => ({
      ...prevData,
      tie_on: {
        md: 0,
        tvd: 0,
        inc: 0,
        az: 0,
        ns: 0,
        ew: 0,
      },
    }));
  };

  const handleNextClick = () => {
    if (validateRunForm()) {
      setModalActiveTab('tieon');
    }
  };

  const handleNewRunSubmit = async (e) => {
    e.preventDefault();

    const determineWellType = (inclination) => {
      if (!inclination) return 1;
      const numInclination = parseFloat(inclination);
      if (numInclination <= 5) return 1;
      return 2;
    };

    try {
      const runPayload = {
        job: parseInt(jobId),
        well: wellInfo?.id ? parseInt(wellInfo.id) : null,
        survey_from: parseFloat(newRunData.survey_from) || 0,
        survey_to: parseFloat(newRunData.survey_to) || 0,
        survey_meterage: parseFloat(newRunData.survey_meterage) || 0,
        survey_type: parseInt(newRunData.survey_type),
        well_type: determineWellType(newRunData.well_type),
        proposed_direction: parseFloat(newRunData.proposed_direction) || null,
        elevation_referred_to: newRunData.elevation_referred_to || "Mean Sea Level(MSL)",
        reference_datum: newRunData.reference_datum || "RKB/DFE(Drill Floor Elevation)",
        reference_height: parseFloat(newRunData.reference_height) || null,
        geodetic_system: newRunData.geodetic_system || "Universal Transverse Mercator",
        geodetic_datum: newRunData.geodetic_datum || "PSD 93",
        map_zone: newRunData.map_zone || "Zone 40N(54E to 60E)",
        max_depth: parseFloat(newRunData.max_depth) || null,
        max_inclination: parseFloat(newRunData.max_inclination) || null,
        max_temp: parseFloat(newRunData.max_temp) || null,
        north_reference: parseInt(newRunData.north_reference),
        exp_inclination: newRunData.exp_inclination || null,
        hole_section: parseInt(newRunData.hole_section),
        survey_run_in: parseInt(newRunData.survey_run_in),
        minimum_id: parseInt(newRunData.minimum_id),
      };

      const runResponse = await API.post('/runs/', runPayload);
      const createdRun = runResponse.data;

      const tieOnPayload = {
        run: createdRun.id,
        md: parseFloat(newRunData.tie_on.md) || 0,
        tvd: parseFloat(newRunData.tie_on.tvd) || 0,
        inc: parseFloat(newRunData.tie_on.inc) || 0,
        az: parseFloat(newRunData.tie_on.az) || 0,
        ns: parseFloat(newRunData.tie_on.ns) || 0,
        ew: parseFloat(newRunData.tie_on.ew) || 0,
      };

      await API.post('/tieons/', tieOnPayload);

      toast.success("New run created successfully!");
      onRunCreated({...createdRun, tie_on: tieOnPayload});
      onClose();

    } catch (error) {
      console.error("Failed to create new run:", error);
      const errorMessage = error.response?.data 
        ? JSON.stringify(error.response.data) 
        : error.message;
      toast.error(`Failed to create new run: ${errorMessage}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h2 className="text-xl font-bold">Create New Run</h2>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <div className="flex border-b border-gray-200 mb-4">
            <button
              className={`px-4 py-2 font-medium ${
                modalActiveTab === 'run' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500'
              }`}
              onClick={() => setModalActiveTab('run')}
            >
              Run
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                modalActiveTab === 'tieon' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500'
              } ${!isRunFormComplete ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => isRunFormComplete && setModalActiveTab('tieon')}
              disabled={!isRunFormComplete}
            >
              Tie On
            </button>
          </div>

          <form onSubmit={handleNewRunSubmit}>
            {modalActiveTab === 'run' && (
              <div className="mb-4 text-sm">
                <h3 className="font-semibold text-gray-700 mb-3 border-b pb-1 text-base">Run Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Survey From (m)</label>
                    <input
                      type="number"
                      name="survey_from"
                      value={newRunData.survey_from}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Survey To (m)</label>
                    <input
                      type="number"
                      name="survey_to"
                      value={newRunData.survey_to}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Survey Meterage (m)</label>
                    <input
                      type="number"
                      name="survey_meterage"
                      value={newRunData.survey_meterage}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Survey Type</label>
                    <select
                      name="survey_type"
                      value={newRunData.survey_type}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    >
                      <option value="">Select Survey Type</option>
                      {surveyTypes.map((type) => (
                        <option key={type.survey_type_id} value={type.survey_type_id}>
                          {type.survey_type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Survey Run In</label>
                    <select
                      name="survey_run_in"
                      value={newRunData.survey_run_in}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    >
                      <option value="">Select Survey Run In</option>
                      {surveyRunIns.map((item) => (
                        <option key={item.survey_runin_id} value={item.survey_runin_id}>
                          {item.survey_runin_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hole Section</label>
                    <select
                      name="hole_section"
                      value={newRunData.hole_section}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    >
                      <option value="">Select Hole Section</option>
                      {holeSections.map((item) => (
                        <option key={item.hole_section_id} value={item.hole_section_id}>
                          {item.hole_section_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Minimum ID</label>
                    <select
                      name="minimum_id"
                      value={newRunData.minimum_id}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      required
                    >
                      <option value="">Select Minimum ID</option>
                      {minimumIds.map((item) => (
                        <option key={item.minimum_id} value={item.minimum_id}>
                          {item.minimum_id_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {[
                    ["Proposed Direction", "proposed_direction"],
                    ["Expected temperature", "exp_temp"],
                    ["Reference Height", "reference_height"],
                  ].map(([label, name]) => (
                    <div key={name}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <input
                        type={name === "reference_height" ? "number" : "text"}
                        name={name}
                        value={newRunData[name]}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleNextClick}
                    disabled={!isRunFormComplete}
                    className={`px-2 py-1 rounded text-xs ${
                      isRunFormComplete
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {modalActiveTab === 'tieon' && (
              <div className="mb-4 text-sm">
                <h3 className="font-semibold text-gray-700 mb-3 border-b pb-1 text-base">Tie On Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ["Measured Depth (m)", "md"],
                    ["TVD (m)", "tvd"],
                    ["Inclination (deg)", "inc"],
                    ["Azimuth (deg)", "az"],
                    ["NS (m)", "ns"],
                    ["EW (m)", "ew"]
                  ].map(([label, name]) => (
                    <div key={name}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <input
                        type="number"
                        name={name}
                        value={newRunData.tie_on[name]}
                        onChange={handleTieOnChange}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setModalActiveTab('run')}
                    className="px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    Back
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleTieOnFromSurface}
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                  >
                    Tie On from Surface
                  </button>

                  <button
                    type="submit"
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                  >
                    Create Run
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}